import React, { useCallback, useEffect, useState } from "react";
import * as tokenService from "./tokenService";
import { refreshSession } from "./refreshManager";
import { startSessionManager, stopSessionManager, resetIdle } from "./sessionManager";
import { performLogout, handleSessionExpired, subscribeToLogoutBroadcast } from "./logout";
import SessionExpiryModal from "../components/SessionExpiryModal";

/**
 * Session lifecycle host — mount ONCE inside each app's authenticated layout
 * (never around public/login routes).
 *
 * SHARED FILE — byte-identical in prx_shell-ui, prx_buyerapp-ui,
 * prx_buyer_WorkOrder_ui and prx_master-ui (canonical: prx_buyerapp-ui).
 *
 * On mount: silent bootstrap refresh (browser reopen / deep link with an
 * expired access token), inactivity tracking wired to the expiry modal, and
 * the cross-tab logout subscription.
 */

const AuthSessionProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [warnDeadline, setWarnDeadline] = useState<number | null>(null);

  useEffect(() => {
    if (tokenService.isAuthenticated() && tokenService.isAccessTokenExpired()) {
      // Non-blocking: the interceptor's 401 path covers any request that
      // races this.
      void refreshSession();
    }

    startSessionManager({
      onWarn: (deadline) => setWarnDeadline(deadline),
      onWarnCleared: () => setWarnDeadline(null),
      onExpire: () => {
        setWarnDeadline(null);
        void performLogout("idle");
      },
    });
    const unsubscribe = subscribeToLogoutBroadcast();

    return () => {
      stopSessionManager();
      unsubscribe();
    };
  }, []);

  const handleStay = useCallback(async () => {
    const refreshed = await refreshSession();
    setWarnDeadline(null);
    if (refreshed) {
      resetIdle();
    } else {
      handleSessionExpired();
    }
  }, []);

  const handleSignOut = useCallback(() => {
    setWarnDeadline(null);
    void performLogout("user");
  }, []);

  return (
    <>
      {children}
      <SessionExpiryModal
        open={warnDeadline != null}
        deadline={warnDeadline ?? 0}
        onStay={handleStay}
        onSignOut={handleSignOut}
      />
    </>
  );
};

export default AuthSessionProvider;
